import Decimal from "decimal.js";
import {
  DimensionUnitEnum,
  dimensionUnitFactor,
  type CustomFieldValue,
  type DimensionUnit,
  type PublicQuote,
} from "@forgepoint/shared";

/** One populated escalator slot, including its quote-wide projected total. */
export interface ActiveEscalator {
  pct: number;
  dateLabel: string;
  total: Decimal;
  ordinalLabel: string;
}

/** Builds up to six manually entered escalators, preserving slot order and cumulative pricing. */
export function getSteelDistributorEscalators(
  quote: Pick<PublicQuote, "customFields" | "lines" | "createdAt">,
): ActiveEscalator[] {
  const customFields = quote.customFields ?? {};
  const slots = ["1ST %", "2ND %", "3RD %", "4TH %", "5TH %", "6TH %"]
    .map((ordinalLabel, index) => ({
      pct: parseEscalatorPercent(
        customFields[`Escalator Percent ${index + 1}`],
      ),
      date: asString(customFields[`Escalator Date ${index + 1}`]),
      ordinalLabel,
    }))
    .map((slot, index) => ({
      ...slot,
      active: slot.pct !== 0 && (index === 0 || !!slot.date),
    }));
  if (!slots.some((slot) => slot.active)) {
    return [];
  }
  const totals = computeEscalatorTotals(
    quote.lines ?? [],
    slots.map((slot) => (slot.active ? slot.pct : 0)),
  );
  return slots.flatMap((slot, index) => {
    if (!slot.active) {
      return [];
    }
    return [
      {
        pct: slot.pct,
        dateLabel: slot.date
          ? `${slot.pct}% INCREASE FOR ${slot.date.toUpperCase()}`
          : getEscalatorFallbackLabel(quote.createdAt, slot.pct),
        total: totals[index],
        ordinalLabel: slot.ordinalLabel,
      },
    ];
  });
}

/**
 * Converts a raw item length to feet using its dimension unit.
 * @returns The length in feet, or null if the inputs are missing/invalid.
 */
function resolveItemLengthInFeet(
  itemLength: string | null | undefined,
  dimensionUnit: DimensionUnit | null | undefined,
): Decimal | null {
  if (!itemLength || !dimensionUnit) {
    return null;
  }
  const rawLength = new Decimal(itemLength);
  if (rawLength.isZero() || rawLength.isNeg()) {
    return null;
  }
  return rawLength.times(
    dimensionUnitFactor(dimensionUnit, DimensionUnitEnum.feet),
  );
}

/**
 * Calculates the MLF (per 1000 linear feet) price.
 * Formula: unitPrice * 1000 / lengthInFeet
 */
export function calculateMlf(
  unitPrice: string | null | undefined,
  itemLength: string | null | undefined,
  dimensionUnit: DimensionUnit | null | undefined,
): Decimal | null {
  if (!unitPrice) {
    return null;
  }
  const lengthInFeet = resolveItemLengthInFeet(itemLength, dimensionUnit);
  if (!lengthInFeet) {
    return null;
  }
  return new Decimal(unitPrice).times(1000).dividedBy(lengthInFeet);
}

/** Applies an escalator percentage to a base price. */
export function applyEscalator(
  basePrice: Decimal,
  escalatorPercent: number,
): Decimal {
  return basePrice.times(
    new Decimal(1).plus(new Decimal(escalatorPercent).dividedBy(100)),
  );
}

/** Safely extracts a string from a custom field value. */
export function asString(value: CustomFieldValue | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.join(", ") || null;
  }
  return String(value) || null;
}

/** Parses an escalator percentage from a custom field value. */
function parseEscalatorPercent(value: CustomFieldValue | undefined): number {
  if (value === null || value === undefined || Array.isArray(value)) {
    return 0;
  }
  const num = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isNaN(num) ? 0 : num;
}

/** Computes total escalated amounts across all item lines using the existing MLF rounding. */
function computeEscalatorTotals(
  lines: NonNullable<PublicQuote["lines"]>,
  percentages: number[],
): Decimal[] {
  let cumulativePct = 0;
  const cumulativePercentages = percentages.map(
    (pct) => (cumulativePct += pct),
  );
  const totals = percentages.map(() => new Decimal(0));
  for (const line of lines) {
    if (line.type !== "ITEM") {
      continue;
    }
    for (const qb of line.quantityBreaks) {
      const quantity = qb.quantity?.trim();
      const unitPrice = qb.unitPriceOverride ?? qb.unitPrice;
      if (!quantity || !unitPrice) {
        continue;
      }
      const qty = new Decimal(quantity);
      const lengthInFeet = resolveItemLengthInFeet(
        line.item?.length,
        line.item?.dimensionUnit,
      );
      const mlf = calculateMlf(
        unitPrice,
        line.item?.length,
        line.item?.dimensionUnit,
      );
      for (const [index, pct] of cumulativePercentages.entries()) {
        const price =
          mlf && lengthInFeet
            ? applyEscalator(mlf, pct)
                .floor()
                .times(lengthInFeet)
                .dividedBy(1000)
            : applyEscalator(new Decimal(unitPrice), pct);
        totals[index] = totals[index].plus(qty.times(price));
      }
    }
  }
  return totals;
}

/** Generates a fallback escalator date label (Jan 1 of next year) when no manual date is set. */
function getEscalatorFallbackLabel(
  effectiveDate: Date | string | null | undefined,
  pct: number,
): string {
  const date = effectiveDate
    ? effectiveDate instanceof Date
      ? effectiveDate
      : new Date(effectiveDate)
    : new Date();
  const baseYear = date.getFullYear() + 1;
  return `${pct}% INCREASE FOR JANUARY 1ST, ${baseYear}`;
}
